import { DOCUMENT, isPlatformServer } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject } from 'rxjs';
import { HTML } from '~/core/helpers/html.helper';
import { environment } from '~environments/environment';

declare global {
  interface Window {
    gdprAppliesGlobally: boolean;
    attachEvent(event: string, listener: EventListener): boolean;
    didomiConfig?: {
      cookies?: {
        local?: {
          customDomain?: string;
        };
      };
    };
  }
}

@Injectable({
  providedIn: 'root',
})
export class PrivacyManagerService {
  private readonly datadogConsentSubject = new BehaviorSubject<boolean>(false);
  private readonly amplitudeConsentSubject = new BehaviorSubject<boolean>(
    false
  );
  public datadogConsent$ = this.datadogConsentSubject.asObservable();
  public amplitudeConsent$ = this.amplitudeConsentSubject.asObservable();
  public readonly amplitudeScriptSrc = 'amplitude';

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: string,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly cookieService: CookieService
  ) {}

  private updateConsent(): void {
    const didomiToken = localStorage.getItem('didomi_token');

    if (!didomiToken) {
      return;
    }

    const decodedDidomiToken = atob(didomiToken);
    const didomiVendors = JSON.parse(decodedDidomiToken);

    this.cookieService.set(
      'activities-didomiVendors',
      JSON.stringify(didomiVendors.vendors),
      365,
      '/',
      window.location.hostname,
      true,
      'Strict'
    );
    this.updateAmplitudeConsent();
    this.updateDatadogConsent();
  }

  private registerConsentChangeListener(): void {
    window.didomiEventListeners = window.didomiEventListeners || [];
    window.didomiEventListeners.push({
      event: 'consent.changed',
      listener: () => {
        this.updateConsent();
      },
    });
  }

  private updateDatadogConsent(): void {
    const isConsent =
      window.Didomi?.getUserStatus().vendors.consent.enabled.some(
        (item: string) => item.includes('datadog')
      ) ?? false;
    this.datadogConsentSubject.next(isConsent);
  }

  private updateAmplitudeConsent(): void {
    const isConsent =
      window.Didomi?.getUserStatus().vendors.consent.enabled.some(
        (item: string) => item.includes('amplitude')
      ) ?? false;
    this.amplitudeConsentSubject.next(isConsent);
  }

  private initDidomi(): void {
    window.didomiConfig = {
      cookies: {
        local: {
          customDomain: window.location.hostname
        }
      }
    };

    const script = this.document.createElement('script');
    script.innerHTML =
      'window.gdprAppliesGlobally=!0,function t(e,a,r,n,i){function s(t,e,r,n){if("function"==typeof r){window[a]||(window[a]=[]);var s=!1;i&&(s=i(t,e,r)),s||window[a].push({command:t,parameter:e,callback:r,version:n})}}function c(t){if(window[e]&&!0===window[e].stub&&t.data){var a,i="string"==typeof t.data;try{a=i?JSON.parse(t.data):t.data}catch(s){return}if(a[r]){var c=a[r];window[e](c.command,c.parameter,function(e,a){var r={};r[n]={returnValue:e,success:a,callId:c.callId},t.source.postMessage(i?JSON.stringify(r):r,"*")},c.version)}}}s.stub=!0,"function"!=typeof window[e]&&(window[e]=s,window.addEventListener?window.addEventListener("message",c,!1):window.attachEvent("onmessage",c))}("__tcfapi","__tcfapiBuffer","__tcfapiCall","__tcfapiReturn"),function t(e){if(!window.frames[e]){if(document.body&&document.body.firstChild){var a=document.body,r=document.createElement("iframe");r.style.display="none",r.name=e,r.title=e,a.insertBefore(r,a.firstChild)}else setTimeout(function(){t(e)},5)}}("__tcfapiLocator"),function(t){let e=document.createElement("script");e.id="spcloader",e.type="text/javascript",e.async=!0,e.src="https://sdk.privacy-center.org/"+t+"/loader.js?target="+document.location.hostname,(t=document.getElementsByTagName("script")[0]).parentNode.insertBefore(e,t)}("929521d8-0c53-458b-9f07-852d7d00d609");';
    HTML.unshift(this.document.head, script);
  }

  public initAmplitude(): void {
    const amplitudeApiKey = environment.applications.amplitude.apiKey;
    const script = this.document.createElement('script');

    script.innerHTML = `!(function () {
      'use strict';
      !(function (e, t) {
        var r = e.amplitude || { _q: [], _iq: {} };
        if (r.invoked)
          e.console &&
            console.error &&
            console.error('Amplitude snippet has been loaded.');
        else {
          var n = function (e, t) {
              e.prototype[t] = function () {
                return (
                  this._q.push({
                    name: t,
                    args: Array.prototype.slice.call(arguments, 0),
                  }),
                  this
                );
              };
            },
            s = function (e, t, r) {
              return function (n) {
                e._q.push({
                  name: t,
                  args: Array.prototype.slice.call(r, 0),
                  resolve: n,
                });
              };
            },
            o = function (e, t, r) {
              e._q.push({ name: t, args: Array.prototype.slice.call(r, 0) });
            },
            i = function (e, t, r) {
              e[t] = function () {
                if (r)
                  return {
                    promise: new Promise(
                      s(e, t, Array.prototype.slice.call(arguments))
                    ),
                  };
                o(e, t, Array.prototype.slice.call(arguments));
              };
            },
            a = function (e) {
              for (var t = 0; t < m.length; t++) i(e, m[t], !1);
              for (var r = 0; r < g.length; r++) i(e, g[r], !0);
            };
          r.invoked = !0;
          var c = t.createElement('script');
          (c.type = 'text/javascript'),
            (c.integrity =
              'sha384-18zPhxLBEI71e4+e1MPLbXFlMrz+0vYP5j9jpSRJyYwR+0/dFSAt9yTmuMvbWqn0'),
            (c.crossOrigin = 'anonymous'),
            (c.async = !0),
            (c.src =
              'https://cdn.amplitude.com/libs/analytics-browser-2.11.9-min.js.gz'),
            (c.onload = function () {
              e.amplitude.runQueuedFunctions ||
                console.log('[Amplitude] Error: could not load SDK');
            });
          var u = t.getElementsByTagName('script')[0];
          u.parentNode.insertBefore(c, u);
          for (
            var l = function () {
                return (this._q = []), this;
              },
              p = [
                'add',
                'append',
                'clearAll',
                'prepend',
                'set',
                'setOnce',
                'unset',
                'preInsert',
                'postInsert',
                'remove',
                'getUserProperties',
              ],
              d = 0;
            d < p.length;
            d++
          )
            n(l, p[d]);
          r.Identify = l;
          for (
            var v = function () {
                return (this._q = []), this;
              },
              f = [
                'getEventProperties',
                'setProductId',
                'setQuantity',
                'setPrice',
                'setRevenue',
                'setRevenueType',
                'setEventProperties',
              ],
              y = 0;
            y < f.length;
            y++
          )
            n(v, f[y]);
          r.Revenue = v;
          var m = [
              'getDeviceId',
              'setDeviceId',
              'getSessionId',
              'setSessionId',
              'getUserId',
              'setUserId',
              'setOptOut',
              'setTransport',
              'reset',
              'extendSession',
            ],
            g = [
              'init',
              'add',
              'remove',
              'track',
              'logEvent',
              'identify',
              'groupIdentify',
              'setGroup',
              'revenue',
              'flush',
            ];
          a(r),
            (r.createInstance = function (e) {
              return (r._iq[e] = { _q: [] }), a(r._iq[e]), r._iq[e];
            }),
            (e.amplitude = r);
        }
      })(window, document);
    })();

    amplitude.init('${amplitudeApiKey}', {
      defaultTracking: true,
      serverZone: 'EU',
    });`;

    HTML.unshift(this.document.getElementsByTagName('html')[0], script);
  }

  public getUserConsent(): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }

    this.registerConsentChangeListener();

    window.didomiOnReady = window.didomiOnReady || [];
    window.didomiOnReady.push(() => {
      this.updateConsent();
    });
  }

  public removeScriptSrcName(srcName: string): void {
    const scripts = this.document.getElementsByTagName('script');

    for (let i = scripts.length - 1; i >= 0; i--) {
      const script = scripts[i];
      if (script.src?.includes(srcName)) {
        script.parentNode?.removeChild(script);
      }
    }
  }

  public initialize(): void {
    if (isPlatformServer(this.platformId)) {
      return;
    }
    this.initDidomi();
    this.getUserConsent();
    this.updateDatadogConsent();
    this.updateAmplitudeConsent();
  }
}
